/**
 * Merge remote-controller input into the shared input buffer the CPU worker
 * reads each frame. The host tab receives decoded RemoteInputState snapshots
 * over the transport and, on the main thread, folds them into `shared.input`
 * via Atomics — exactly where the local gamepad poller writes (input.ts). The
 * emulator core, the FFI, and the worker are untouched: a phone is just another
 * source publishing into the same buffer.
 *
 * Local gamepad and remote input COMBINE (buttons OR'd, sticks taken from
 * whichever source is non-neutral) so a desktop pad and a phone can be used
 * together, and neither clobbers the other to neutral.
 */

import {
  INPUT_WORD_BUTTONS, INPUT_WORD_LX, INPUT_WORD_LY,
  INPUT_WORD_RX, INPUT_WORD_RY, INPUT_WORD_CONNECTED,
} from "../input";
import type { RemoteInputState } from "./protocol";

/** A neutral snapshot: nothing held, sticks centred. Used when a remote is
 *  connected but its last frame has gone stale (see applyRemoteInput). */
export const REMOTE_NEUTRAL: RemoteInputState = {
  seq: 0, buttons: 0, lx: 0, ly: 0, rx: 0, ry: 0,
};

/** Pick the stick pair that is actually deflected: prefer the remote's axis
 *  when it is non-zero, else keep the local axis. Keeps a resting phone stick
 *  from cancelling a real gamepad deflection and vice-versa. */
function pickAxis(local: number, remote: number): number {
  return remote !== 0 ? remote : local;
}

/**
 * Fold a remote snapshot into `view` (the shared Int32 input buffer), COMBINING
 * it with whatever the local gamepad poller most recently wrote there:
 *   - buttons: OR of local and remote,
 *   - each stick axis: the remote's if deflected, else the local value,
 *   - INPUT_WORD_CONNECTED: max(local count, 1) so the guest sees a controller.
 * All writes go through Atomics.store, matching pollGamepadInto, so the CPU
 * worker's lock-free reader never tears. Returns the merged buttons bitfield
 * for observability (tests read it without re-reading the buffer).
 *
 * Pure w.r.t. timing: the caller decides staleness by passing REMOTE_NEUTRAL
 * instead of a live snapshot when the last frame is too old.
 */
export function applyRemoteInput(view: Int32Array, remote: RemoteInputState): number {
  const localButtons = Atomics.load(view, INPUT_WORD_BUTTONS);
  const localLx = Atomics.load(view, INPUT_WORD_LX);
  const localLy = Atomics.load(view, INPUT_WORD_LY);
  const localRx = Atomics.load(view, INPUT_WORD_RX);
  const localRy = Atomics.load(view, INPUT_WORD_RY);
  const localConnected = Atomics.load(view, INPUT_WORD_CONNECTED);

  const buttons = (localButtons | remote.buttons) >>> 0;
  Atomics.store(view, INPUT_WORD_BUTTONS, buttons);
  Atomics.store(view, INPUT_WORD_LX, pickAxis(localLx, remote.lx));
  Atomics.store(view, INPUT_WORD_LY, pickAxis(localLy, remote.ly));
  Atomics.store(view, INPUT_WORD_RX, pickAxis(localRx, remote.rx));
  Atomics.store(view, INPUT_WORD_RY, pickAxis(localRy, remote.ry));
  if (localConnected < 1) Atomics.store(view, INPUT_WORD_CONNECTED, 1);

  return buttons;
}

/**
 * Stateful receiver that a host wires to its transport: it keeps the freshest
 * in-order snapshot (dropping reordered frames by seq) and, when asked, applies
 * it — or a neutral snapshot if no frame has arrived within `staleMs`, so a
 * dropped connection releases every held button instead of sticking. `nowMs`
 * is injected (Date.now in production) to keep the logic testable.
 */
export class RemoteInputReceiver {
  private latest: RemoteInputState = REMOTE_NEUTRAL;
  private lastSeq = 0;
  private lastRxMs = -Infinity;
  private haveFrame = false;
  private readonly staleMs: number;

  constructor(staleMs = 500) {
    this.staleMs = staleMs;
  }

  /** Ingest a decoded snapshot; keeps it only if strictly newer by seq. */
  ingest(state: RemoteInputState, nowMs: number): void {
    if (this.haveFrame) {
      const newer = ((state.seq - this.lastSeq) >>> 0) < 0x80000000 &&
        state.seq !== this.lastSeq;
      if (!newer) return;
    }
    this.latest = state;
    this.lastSeq = state.seq;
    this.lastRxMs = nowMs;
    this.haveFrame = true;
  }

  /** Apply the freshest snapshot into `view`, or neutral if it has gone stale. */
  apply(view: Int32Array, nowMs: number): void {
    const fresh = this.haveFrame && nowMs - this.lastRxMs <= this.staleMs;
    applyRemoteInput(view, fresh ? this.latest : REMOTE_NEUTRAL);
  }

  /** Drop all state (call on disconnect) so the next apply() is neutral. */
  reset(): void {
    this.latest = REMOTE_NEUTRAL;
    this.lastSeq = 0;
    this.lastRxMs = -Infinity;
    this.haveFrame = false;
  }
}
